import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  ControlGroup,
  Dialog,
  EmptyInputGroup,
  FormField,
  Grid,
  IDialogProps,
  majorScale,
  NumberInput,
  Spinner,
  TextArea,
} from '../../npaper';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { IProduct, IProductStockLocation, useStores } from '../../stores';
import { ILocation } from '../../stores/warehousing/LocationStore';
import LocationSelect from '../warehousing/LocationSelect';

export interface IStockDialogProps extends IDialogProps {
  product: IProduct;
}

interface FormValues {
  quantity: number | null;
  increase: boolean;
  location: number | string;
  note?: string;
}

export const StockDialog: React.FC<IStockDialogProps> = ({
  product,
  ...props
}) => {
  const intl = useIntl();
  const { productStore } = useStores();
  const [isShown, setIsShown] = useState(props.isShown);

  const [stockLocations, setStockLocations] =
    useState<IProductStockLocation[] | null>(null);

  const {
    setValue,
    control,
    reset,
    register,
    handleSubmit,
    formState,
    errors,
  } = useForm<FormValues>();

  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  useEffect(() => {
    if (!isShown) return;
    reset({
      quantity: product.stock,
    });

    let isSub = true;
    productStore
      .getProductLocations(product)
      .then((data: any) => isSub && setStockLocations(data.stock_locations));
    return () => {
      isSub = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, isShown]);

  const onSubmit = async (values: FormValues) => {
    if (values.quantity === null) return;
    try {
      await productStore.updateStock(
        product,
        values.quantity,
        values.location,
        values.note
      );
      props.onClose();
    } catch (err) {
      // TODO: Handle error
    }
  };

  return (
    <Dialog
      isShown={isShown}
      {...props}
      title={intl.formatMessage({ defaultMessage: 'Update stock' })}
      actions={
        <>
          <Button
            onClick={() => props.onClose()}
            disabled={formState.isSubmitting}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            <FormattedMessage defaultMessage="Update stock" />
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <FormField className="col-span-9" inline label="">
          {!stockLocations ? (
            <Spinner size={16} />
          ) : (
            <>
              <Box is="p" className="muted medium" marginTop={majorScale(1)}>
                <FormattedMessage defaultMessage="Already stocked locations" />
              </Box>
              {stockLocations.length === 0 && (
                <p className="muted">
                  <FormattedMessage defaultMessage="No locations with stock" />
                </p>
              )}
              {stockLocations.map((loc) => (
                <Box
                  key={loc.location}
                  display="flex"
                  alignItems="baseline"
                  justifyContent="space-between"
                  marginTop={majorScale(0.5)}
                >
                  <p
                    className="a link muted"
                    onClick={() => {
                      setValue('location', loc.location);
                      setValue('quantity', loc!.stock);
                    }}
                  >
                    {loc.location_code}
                  </p>
                  <p className="muted">
                    <FormattedUnit unit={product.unit} value={loc.stock} />
                  </p>
                </Box>
              ))}
            </>
          )}
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Location' })}
          inline
          isInvalid={!!errors.location}
          hint={errors.location?.message}
        >
          <Controller
            rules={{
              required: intl.formatMessage({
                defaultMessage: 'Please select a location',
              }),
            }}
            control={control}
            name="location"
            render={({ onChange, onBlur, value }) => (
              <LocationSelect
                onOpen={onBlur}
                onSelect={(location: ILocation) => {
                  onChange(location.id);
                  if (
                    stockLocations &&
                    stockLocations.find((l) => l.location === location.id)
                  ) {
                    const loc = stockLocations.find(
                      (l) => l.location === location.id
                    );
                    setValue('quantity', loc!.stock);
                  } else {
                    setValue('quantity', 0);
                  }
                }}
                value={value}
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({
            defaultMessage: 'Update stock to',
          })}
          inline={true}
        >
          <ControlGroup>
            <Controller
              control={control}
              name="quantity"
              defaultValue={null}
              render={({ onChange, value }) => (
                <NumberInput
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Set to quantity',
                  })}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <EmptyInputGroup>
              <FormattedUnit unit={product.unit} unitDisplay="long" />
            </EmptyInputGroup>
          </ControlGroup>
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({
            defaultMessage: 'Note',
          })}
          inline={true}
        >
          <TextArea
            name="note"
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter a brief note',
            })}
            validationMessage={errors.note && errors.note.message}
            isInvalid={!!errors.note}
            ref={register({
              maxLength: {
                value: 300,
                message: intl.formatMessage({
                  defaultMessage:
                    'Please enter a note no longer than 300 characters',
                }),
              },
            })}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};

interface MoveFormValues {
  quantity: number;
  maxQuantity: number | null;
  from_location: number | string;
  to_location: number | string;
  note?: string;
}

export const MoveStockDialog: React.FC<IStockDialogProps> = ({
  product,
  ...props
}) => {
  const intl = useIntl();
  const { productStore } = useStores();
  const [isShown, setIsShown] = useState(props.isShown);

  const [stockLocations, setStockLocations] =
    useState<IProductStockLocation[] | null>(null);

  const {
    watch,
    setValue,
    register,
    control,
    reset,
    handleSubmit,
    formState,
    errors,
  } = useForm<MoveFormValues>();

  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  useEffect(() => {
    if (!isShown) return;
    reset({
      quantity: product.stock || 0,
    });

    let isSub = true;
    productStore
      .getProductLocations(product)
      .then((data: any) => isSub && setStockLocations(data.stock_locations));
    return () => {
      isSub = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, isShown]);

  const onSubmit = async (values: MoveFormValues) => {
    if (values.quantity === null) return;
    try {
      await productStore.moveStock(product, values);
      props.onClose();
    } catch (err) {
      // TODO: Handle error
    }
  };

  return (
    <Dialog
      isShown={isShown}
      {...props}
      title={intl.formatMessage({ defaultMessage: 'Move stock' })}
      actions={
        <>
          <Button
            onClick={() => props.onClose()}
            disabled={formState.isSubmitting}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            <FormattedMessage defaultMessage="Move stock" />
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <FormField className="col-span-9" inline label="">
          {!stockLocations ? (
            <Spinner size={16} />
          ) : (
            <>
              <Box is="p" className="muted medium" marginTop={majorScale(1)}>
                <FormattedMessage defaultMessage="Already stocked locations" />
              </Box>
              {stockLocations.length === 0 && (
                <p className="muted">
                  <FormattedMessage defaultMessage="No locations with stock" />
                </p>
              )}
              {stockLocations.map((loc) => (
                <Box
                  key={loc.location}
                  display="flex"
                  alignItems="baseline"
                  justifyContent="space-between"
                  marginTop={majorScale(0.5)}
                >
                  <p
                    className="a link muted"
                    onClick={() => {
                      setValue('from_location', loc.location);
                      setValue('quantity', loc.stock);
                      setValue('maxQuantity', loc.stock);
                    }}
                  >
                    {loc.location_code}
                  </p>
                  <p className="muted">
                    <FormattedUnit unit={product.unit} value={loc.stock} />
                  </p>
                </Box>
              ))}
            </>
          )}
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'From location' })}
          inline
          isInvalid={!!errors.from_location}
          hint={errors.from_location?.message}
        >
          <Controller
            rules={{
              required: intl.formatMessage({
                defaultMessage: 'Please select a location',
              }),
            }}
            control={control}
            name="from_location"
            render={({ onChange, onBlur, value }) => (
              <LocationSelect
                onOpen={onBlur}
                onSelect={(location: ILocation) => {
                  onChange(location.id);
                  if (
                    stockLocations &&
                    stockLocations.find((l) => l.location === location.id)
                  ) {
                    const loc = stockLocations.find(
                      (l) => l.location === location.id
                    );
                    setValue('maxQuantity', loc!.stock);
                    setValue('quantity', loc!.stock);
                  } else {
                    setValue('quantity', 0);
                  }
                }}
                value={value}
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'To location' })}
          inline
          isInvalid={!!errors.to_location}
          hint={errors.to_location?.message}
        >
          <Controller
            rules={{
              required: intl.formatMessage({
                defaultMessage: 'Please select a location',
              }),
              validate: (data) => {
                if (data === watch('from_location')) {
                  return intl.formatMessage({
                    defaultMessage: 'Cannot move stock to the same location',
                  });
                }
                return undefined;
              },
            }}
            control={control}
            name="to_location"
            render={({ onChange, onBlur, value }) => (
              <LocationSelect
                onOpen={onBlur}
                onSelect={(location: ILocation) => {
                  onChange(location.id);
                }}
                value={value}
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({
            defaultMessage: 'Stock to move',
          })}
          inline={true}
          isInvalid={!!errors.quantity}
          hint={errors.quantity && errors.quantity.message}
        >
          <ControlGroup>
            <Controller
              control={control}
              name="quantity"
              defaultValue={null}
              rules={{
                validate: (data) => {
                  if (data > (watch('maxQuantity') || product.stock || 0)) {
                    return intl.formatMessage({
                      defaultMessage:
                        'You cannot move more stock than is available',
                    });
                  } else {
                    return undefined;
                  }
                },
              }}
              render={({ onChange, value }) => (
                <NumberInput
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Provide quantity',
                  })}
                  value={value}
                  onChange={onChange}
                  max={watch('maxQuantity') || undefined}
                />
              )}
            />
            <EmptyInputGroup>
              <FormattedUnit unit={product.unit} unitDisplay="long" />
            </EmptyInputGroup>
          </ControlGroup>
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({
            defaultMessage: 'Note',
          })}
          inline={true}
        >
          <TextArea
            name="note"
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter a brief note',
            })}
            validationMessage={errors.note && errors.note.message}
            isInvalid={!!errors.note}
            ref={register({
              maxLength: {
                value: 300,
                message: intl.formatMessage({
                  defaultMessage:
                    'Please enter a note no longer than 300 characters',
                }),
              },
            })}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};
